import { useCallback, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import {
  INVESTOR_WIZARD_STEP_FIELDS,
  INVESTOR_WIZARD_STEP_IDS,
  type InvestorWizardFormData,
  type InvestorWizardFormValues,
} from "@/domain/validation/investorSchemas";

interface UseAddInvestorWizardStepsParams {
  form: UseFormReturn<InvestorWizardFormData, unknown, InvestorWizardFormValues>;
}

export function useAddInvestorWizardSteps({ form }: UseAddInvestorWizardStepsParams) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  const triggerCurrentStep = useCallback(async () => {
    const stepId = INVESTOR_WIZARD_STEP_IDS[currentStep];
    if (!stepId) return false;
    const fields = INVESTOR_WIZARD_STEP_FIELDS[stepId];
    return form.trigger(fields);
  }, [currentStep, form]);

  const resetSteps = useCallback(() => {
    setCurrentStep(0);
    setCompletedSteps([]);
  }, []);

  const goNext = useCallback(async () => {
    const isValid = await triggerCurrentStep();
    if (isValid) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps((prev) => [...prev, currentStep]);
      }
      if (currentStep < INVESTOR_WIZARD_STEP_IDS.length - 1) setCurrentStep((s) => s + 1);
    }
  }, [currentStep, completedSteps, triggerCurrentStep]);

  const goPrevious = useCallback((): boolean => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
      return true;
    }
    return false;
  }, [currentStep]);

  const goToStep = useCallback(
    (stepIndex: number): boolean => {
      const maxCompleted = completedSteps.length > 0 ? Math.max(...completedSteps) : -1;
      if (stepIndex <= maxCompleted + 1) {
        setCurrentStep(stepIndex);
        return true;
      }
      return false;
    },
    [completedSteps],
  );

  return {
    currentStep,
    completedSteps,
    triggerCurrentStep,
    resetSteps,
    goNext,
    goPrevious,
    goToStep,
  };
}
